import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, patch, post } from "@/lib/api";
import type { FileRow, FileTargetType, SubmittedFile } from "./types";

export const fileKeys = {
  all: ["files"] as const,
  workspace: (ws: string) => ["files", "ws", ws] as const,
  everywhere: ["files", "everywhere"] as const,
  target: (type: string, id: string) => ["files", "target", type, id] as const,
};

/** Every file uploaded in a workspace (plus read-only submitted copies). */
export function useWorkspaceFiles(workspaceId: string | undefined) {
  return useQuery({
    queryKey: fileKeys.workspace(workspaceId ?? ""),
    queryFn: () => get<{ files: FileRow[]; submittedFiles: SubmittedFile[] }>(`/api/workspaces/${workspaceId}/files`),
    enabled: !!workspaceId,
  });
}

/** Every file in every notebook and project the person can open (the Files page). */
export function useAllFiles(enabled: boolean) {
  return useQuery({
    queryKey: fileKeys.everywhere,
    queryFn: () => get<{ files: FileRow[]; submittedFiles: SubmittedFile[] }>("/api/files/all"),
    enabled,
  });
}

/** Files owned by or linked to one item. */
export function useTargetFiles(targetType: FileTargetType, targetId: string | undefined) {
  return useQuery({
    queryKey: fileKeys.target(targetType, targetId ?? ""),
    queryFn: async () => (await get<{ files: FileRow[] }>(`/api/files?targetType=${targetType}&targetId=${targetId}`)).files,
    enabled: !!targetId,
  });
}

export function useInvalidateFiles() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: fileKeys.all });
}

export function useFileMutations() {
  const invalidate = useInvalidateFiles();
  const opts = { onSettled: invalidate };
  return {
    link: useMutation({
      mutationFn: (v: { fileId: string; targetType: FileTargetType; targetId: string }) => post(`/api/files/${v.fileId}/links`, { targetType: v.targetType, targetId: v.targetId }),
      ...opts,
    }),
    unlink: useMutation({ mutationFn: (v: { fileId: string; linkId: string }) => del(`/api/files/${v.fileId}/links/${v.linkId}`), ...opts }),
    describe: useMutation({ mutationFn: (v: { fileId: string; description: string | null }) => patch(`/api/files/${v.fileId}`, { description: v.description }), ...opts }),
    remove: useMutation({ mutationFn: (fileId: string) => del<{ ok: true; note?: string }>(`/api/files/${fileId}`), ...opts }),
  };
}
